<script setup lang="ts">
import { isNextToElementPostition, useTutorialStore, type Step } from '../store-pyr';
import { useLayersStore, setStateRewriter } from 'src/store';
import { marked } from 'marked';
import { RESOURCES_MENU_SVG } from '../data/toolbar-icons';
import { computed, nextTick, onMounted, onUnmounted, ref, useTemplateRef, watch } from 'vue';
import { useProofreadingBackendStore } from '../store';
import { liveEdit, loadSiteTexts, wordsToHtml } from '../tutorial_edits';
import TutorialEditDialog from 'components/TutorialEditDialog.vue';

const layerStore = useLayersStore();

const { loadState } = layerStore;

const root = ref<HTMLElement | null>(null);

// The intro tutorial's advance buttons teach the Enter shortcut (Ames,
// 2026-09-29). Not in Merge or Cut, where Enter submits the edit.
const tutorialStore = useTutorialStore();

/** The annotation layers in view that hold at least one annotation. */
function playerAnnotationLayers(): any[] {
    try {
        const layers = (window as any).viewer?.state?.toJSON()?.layers;
        const list = Array.isArray(layers) ? layers : Object.entries(layers ?? {}).map(([name, l]: [string, any]) => ({ ...l, name }));
        return list.filter((l: any) => l?.type === 'annotation' && Array.isArray(l.annotations) && l.annotations.length > 0);
    } catch { return []; }
}
/** A saved view with these layers added, unless it has one of that name. */
function withLayers(state: any, keep: any[]): any {
    if (Array.isArray(state.layers)) {
        const names = new Set(state.layers.map((l: any) => l.name));
        state.layers.push(...keep.filter(l => !names.has(l.name)));
    } else if (state.layers && typeof state.layers === 'object') {
        for (const l of keep) if (!(l.name in state.layers)) { const { name, ...rest } = l; state.layers[name] = rest; }
    }
    return state;
}
const nextTip = computed(() => tutorialStore.activeTutorial === 1 ? 'Protip: press Enter to advance' : undefined);


const props = defineProps<{
    step: Step,
    first: boolean,
    last: boolean,
    stepIndex: number,
    totalSteps: number,
    /** "Part 1 of 4 · Mark what you see": stepIndex and totalSteps then count within it. */
    partLabel?: string,
    /** The box's place in its whole tutorial, for its saved rewording (tutorial_edits.ts). */
    boxIndex?: number,
}>();

// Admins reword a box with the pencil (Ames 2026-10-25). An approved edit
// shows in place of the code's title and words.
const backend = useProofreadingBackendStore();
void loadSiteTexts();
const tutorialNum = tutorialStore.activeTutorial;
const savedEdit = computed(() => props.boxIndex == null ? null : liveEdit(tutorialNum, props.boxIndex, props.step));
const shownTitle = computed(() => savedEdit.value ? savedEdit.value.title : computedStep.value.title);
const shownHtml = computed(() => savedEdit.value ? wordsToHtml(savedEdit.value.words, savedEdit.value.kind) : computedStep.value.html);
const editing = ref(false);

const emit = defineEmits<{
    next: [],
    back: [],
    exitIntro: [],
}>();

// Circuit complexity: steps 0-4 grow increasingly complex, step 5+ revert to simple
const circuitLevel = computed(() => Math.min(props.stepIndex, 4));

function circuitPath(level: number): string {
    // Each level adds more branches to the left-side circuit trace
    const paths = [
        // Level 0: simple line with one node
        'M2,10 L2,90 M0,50 L4,50',
        // Level 1: line with two branch nodes
        'M2,8 L2,92 M0,30 L8,30 M0,65 L6,65 L6,72',
        // Level 2: more branches
        'M2,5 L2,95 M0,25 L10,25 L10,18 M0,50 L8,50 M0,75 L12,75 L12,68 L16,68',
        // Level 3: complex branching
        'M2,3 L2,97 M0,20 L10,20 L10,12 L14,12 M0,40 L8,40 L8,34 M0,60 L12,60 M0,80 L10,80 L10,88 L16,88',
        // Level 4: full circuit tree
        'M2,2 L2,98 M0,15 L10,15 L10,8 L16,8 M10,15 L16,22 M0,35 L8,35 L8,28 M0,50 L14,50 L14,42 L18,42 M14,50 L18,56 M0,70 L10,70 M0,85 L12,85 L12,78 L16,78 M12,85 L16,92',
    ];
    return paths[level] || paths[0];
}

interface ComputedStep {
    first: boolean,
    last: boolean,
    title?: string,
    titleIcon?: string,
    text?: string,
    html?: string,
    video?: string,
    image?: string,
    left: string,
    top: string,
    cssClass?: string,
    modal: boolean,
    noborder: boolean,
    videoCache?: HTMLVideoElement,
    nextLabel?: string,
    floatingImage?: string,
}

const inExitConfirm = ref(false);

function launchConfetti() {
    const canvas = document.createElement('canvas');
    canvas.style.cssText = 'position:fixed;top:0;left:0;width:100vw;height:100vh;z-index:9999;pointer-events:none';
    document.body.appendChild(canvas);
    const ctx = canvas.getContext('2d')!;
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const colors = ['#ff6b9d', '#c44dff', '#00d4ff', '#ffd700', '#50fa7b', '#ff9500', '#7ecaff', '#ff4757'];
    const particles: { x: number; y: number; vx: number; vy: number; size: number; color: string; rotation: number; rv: number; shape: number; life: number }[] = [];

    for (let i = 0; i < 200; i++) {
        particles.push({
            x: canvas.width / 2 + (Math.random() - 0.5) * 200,
            y: canvas.height / 2,
            vx: (Math.random() - 0.5) * 20,
            vy: Math.random() * -18 - 4,
            size: Math.random() * 8 + 3,
            color: colors[Math.floor(Math.random() * colors.length)],
            rotation: Math.random() * Math.PI * 2,
            rv: (Math.random() - 0.5) * 0.3,
            shape: Math.floor(Math.random() * 3),
            life: 1,
        });
    }

    let frame = 0;
    function animate() {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        let alive = false;
        for (const p of particles) {
            if (p.life <= 0) continue;
            alive = true;
            p.x += p.vx;
            p.y += p.vy;
            p.vy += 0.4;
            p.vx *= 0.99;
            p.rotation += p.rv;
            p.life -= 0.006;

            ctx.save();
            ctx.translate(p.x, p.y);
            ctx.rotate(p.rotation);
            ctx.globalAlpha = Math.max(0, p.life);
            ctx.fillStyle = p.color;

            if (p.shape === 0) {
                ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size * 0.6);
            } else if (p.shape === 1) {
                ctx.beginPath();
                ctx.arc(0, 0, p.size / 2, 0, Math.PI * 2);
                ctx.fill();
            } else {
                ctx.beginPath();
                ctx.moveTo(0, -p.size / 2);
                ctx.lineTo(p.size / 2, p.size / 2);
                ctx.lineTo(-p.size / 2, p.size / 2);
                ctx.fill();
            }
            ctx.restore();
        }
        frame++;
        if (alive && frame < 300) {
            requestAnimationFrame(animate);
        } else {
            canvas.remove();
        }
    }
    requestAnimationFrame(animate);
}

const computedStep = ref<ComputedStep>({
    first: props.first,
    last: props.last,
    left: '',
    top: '',
    modal: false,
    text: props.step.text,
    noborder: false,
});

const chipBounds = ref({ top: 'auto', left: 'auto', width: 'inherit' });

async function waitForElement(selector: string, timeout = 3000): Promise<Element | null> {
    const el = document.querySelector(selector);
    if (el) return el;
    return new Promise((resolve) => {
        const start = Date.now();
        const interval = setInterval(() => {
            const el = document.querySelector(selector);
            if (el || Date.now() - start > timeout) {
                clearInterval(interval);
                resolve(el);
            }
        }, 100);
    });
}

/** A box width that grows with the window, like its text: the pixel width
 *  it was written for up to about 1750px wide, wider beyond that. */
function wider(width: string): string {
    const m = /^(\d+(?:\.\d+)?)px$/.exec(width.trim());
    return m ? `max(${m[1]}px, ${(Number(m[1]) / 1750 * 100).toFixed(2)}vw)` : width;
}

async function updateChipPosition() {
    const step = props.step;

    let left = '';
    let top = '';
    let cssClass = undefined;

    const element = await waitForElement(step.position.element);
    // The layer bar and panels settle just after a step's view loads: wait
    // for the target to hold still, so the box opens in place instead of
    // sliding over a moment later (Ames 2026-10-19).
    if (element) await targetSettled(element);
    if (element) {
        const rect = element.getBoundingClientRect();

        if (isNextToElementPostition(step.position)) {
            cssClass = step.position.side;

            const { x: xOff, y: yOff } = step.position.offset || { x: 0, y: 0 };

            if (step.position.side === 'right') {
                left = `${rect.right - xOff}px`;
                top = `${rect.top + rect.height / 2 + yOff}px`;
            } else if (step.position.side === 'left') {
                left = `${rect.left + xOff}px`;
                top = `${rect.top + rect.height / 2 + yOff}px`;
            } else if (step.position.side === 'bottom') {
                left = `${rect.left + rect.width / 2 + xOff}px`;
                top = `${rect.bottom + yOff}px`;
            } else if (step.position.side === 'top') {
                left = `${rect.left + rect.width / 2 + xOff}px`;
                top = `${rect.top - yOff}px`;
            }
        } else {
            cssClass = 'center';
            left = `${step.position.x * rect.width + rect.left}px`;
            top = `${step.position.y * rect.height + rect.top}px`;
        }
    } else {
        // The target is gone (a toolbar icon the user removed, or a UI
        // redesign). Centre the box instead of leaving it stuck in the top
        // left corner, and say which selector failed so audits catch it.
        console.warn('[tutorial] step target not found, centring:', step.position.element);
        cssClass = 'center';
        left = `${window.innerWidth / 2}px`;
        top = `${window.innerHeight / 2}px`;
    }

    chipBounds.value = { top: 'auto', left: 'auto', 'width': 'inherit' };

    if (!step.modal) {
        chipBounds.value.width = wider('350px');
    }

    if (step.width) {
        chipBounds.value.width = wider(step.width);
    }

    let html = step.html;

    if (step.text) {
        html = await marked.parse(step.text);
    }
    // Links to other sites open in a new tab (Ames 2026-10-06), so following
    // one never takes the player out of the tour or the game.
    if (html) {
        html = html.replace(/<a\s+(?![^>]*\btarget=)([^>]*href="https?:[^>]*)>/gi, '<a target="_blank" rel="noopener" $1>');
    }

    computedStep.value = {
        first: props.first,
        last: props.last,
        title: step.title,
        titleIcon: step.titleIcon,
        video: step.video,
        image: step.image,
        html,
        left,
        top,
        cssClass,
        modal: step.modal || false,
        noborder: step.noborder || false,
        nextLabel: step.nextLabel,
        floatingImage: step.floatingImage,
    }

    nextTick(clampChip);
    followTarget(element, step);
}

/** Keep the box on its target while the step shows: the layer bar and
 *  panels can shift after the box opens (Ames 2026-10-19: the pointer sat
 *  beside the +, not under it). */
/** Resolves once the element's place has held for three frames (1 s at most). */
async function targetSettled(el: Element) {
    let last = '', same = 0;
    const t0 = performance.now();
    while (same < 3 && performance.now() - t0 < 1000) {
        await new Promise(r => requestAnimationFrame(r));
        const r = el.getBoundingClientRect();
        const now = `${Math.round(r.left)},${Math.round(r.top)},${Math.round(r.width)}`;
        same = now === last ? same + 1 : 0;
        last = now;
    }
}
let followTimer = 0;
function followTarget(element: Element | null, step: Step) {
    window.clearInterval(followTimer);
    if (!element || !isNextToElementPostition(step.position)) return;
    const pos = step.position;
    // Once the player clicks the target, the box stays put: the click itself
    // can move it (the + slides along when a layer is added).
    const stop = (e: Event) => { if ((e.target as Element | null)?.closest?.(pos.element)) window.clearInterval(followTimer); };
    document.addEventListener('pointerdown', stop, { capture: true, once: false });
    onUnmounted(() => document.removeEventListener('pointerdown', stop, true));
    followTimer = window.setInterval(() => {
        if (dragging.value || !computedStep.value.left) return;
        const el = element.isConnected ? element : document.querySelector(pos.element);
        if (!el) return;
        const rect = el.getBoundingClientRect();
        if (!rect.width && !rect.height) return;
        const { x: xOff, y: yOff } = pos.offset || { x: 0, y: 0 };
        let left = 0, top = 0;
        if (pos.side === 'right') { left = rect.right - xOff; top = rect.top + rect.height / 2 + yOff; }
        else if (pos.side === 'left') { left = rect.left + xOff; top = rect.top + rect.height / 2 + yOff; }
        else if (pos.side === 'bottom') { left = rect.left + rect.width / 2 + xOff; top = rect.bottom + yOff; }
        else { left = rect.left + rect.width / 2 + xOff; top = rect.top - yOff; }
        const l = `${left}px`, t = `${top}px`;
        if (computedStep.value.left !== l || computedStep.value.top !== t) {
            computedStep.value = { ...computedStep.value, left: l, top: t };
        }
    }, 250);
}
onUnmounted(() => window.clearInterval(followTimer));

/** Keep the chip on screen. Additive, so it can run again after the chip
 *  grows (a practice status box or countdown appended under the text used
 *  to push the title off the top). A chip taller than the window pins to
 *  the top and scrolls. */
function clampChip() {
    const el = root.value?.querySelector('.chip');
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const cur = (v: string) => (typeof v === 'string' && v.endsWith('px')) ? parseFloat(v) : 0;
    let top = cur(chipBounds.value.top), left = cur(chipBounds.value.left);
    let changed = false;
    if (rect.top < 0) { top += -rect.top + 8; changed = true; }
    else if (rect.bottom > window.innerHeight && rect.top > 8) {
        top += Math.max(window.innerHeight - rect.bottom - 8, 8 - rect.top); changed = true;
    }
    if (rect.left < 0) { left += -rect.left + 8; changed = true; }
    else if (rect.right > window.innerWidth) { left += window.innerWidth - rect.right - 8; changed = true; }
    if (changed) chipBounds.value = { ...chipBounds.value, top: `${top}px`, left: `${left}px` };
}
function onReclamp() { nextTick(clampChip); }

const ready = ref(false);

// Element currently wearing the .nge-tour-target highlight class so we can
// remove it when the step changes or unmounts.
let highlightedEl: Element | null = null;

async function applyHighlight() {
    // Remove any prior highlight
    if (highlightedEl) {
        highlightedEl.classList.remove('nge-tour-target');
        highlightedEl = null;
    }
    if (!props.step.highlight) return;
    const el = await waitForElement(props.step.position.element);
    if (el) {
        el.classList.add('nge-tour-target');
        highlightedEl = el;
    }
}

// A step must never vanish because its setup stalled (Nseraf 2026-09-29: the
// Advanced Interface tutorial "cuts off abruptly after the press A popup").
// Loading a step's saved view can wait forever on an expired middleauth
// login, or on an animation frame in a background tab; a thrown error did the
// same. Each setup stage gets a time limit and a catch, then the box shows
// anyway while the view keeps loading behind it.
const STEP_SETUP_MS = 8000;
function settleWithin(p: Promise<unknown> | unknown, what: string): Promise<void> {
    return Promise.race([
        Promise.resolve(p).then(() => undefined, (e) => { console.warn(`[tutorial] ${what} failed:`, e); }),
        new Promise<void>(r => setTimeout(() => { console.warn(`[tutorial] ${what} still running after ${STEP_SETUP_MS} ms; showing the step`); r(); }, STEP_SETUP_MS)),
    ]);
}

onMounted(async () => {
    const startT = performance.now();
    if (props.step.state) {
        // Advanced Interface: the annotation layers the player filled keep
        // their points when a step loads its own view (Ames 2026-10-18:
        // "when I hit next it deleted my annotations!").
        // Its views also leave the Selection panel shut: it sat where the
        // layer side panel goes.
        const advanced = tutorialStore.activeTutorial === 2;
        const keep = advanced ? playerAnnotationLayers() : [];
        if (advanced) setStateRewriter(async (st: any) => {
            if (st && typeof st === 'object') st.selection = { ...(st.selection || {}), visible: false };
            return keep.length ? withLayers(st, keep) : st;
        });
        try { await settleWithin(loadState(props.step.state), 'loading the step view'); }
        finally { if (advanced) setStateRewriter(null); }
    }
    if (props.step.clickAfterState) {
        const el = await waitForElement(props.step.clickAfterState);
        if (el) (el as HTMLElement).click();
    }
    if (props.step.onEnter) {
        await settleWithin(props.step.onEnter(), 'step setup');
    }
    console.log('updating position', performance.now() - startT);
    updateChipPosition();
    applyHighlight();
    ready.value = true;
});

// Intercept ENTER to advance tutorial, SPACE for specific steps
// Skip if user is typing in an input/textarea (e.g. coordinate fields)
// While a viewer tool (Cut, Merge, Find Path) is active, Enter is its
// "submit" key, so the tutorial must leave Enter and Space to the viewer.
// Otherwise "press Enter to submit the cut" just advanced the tutorial.
function viewerToolActive(): boolean {
    /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
    const viewer = (window as any)['viewer'];
    try {
        if (viewer?.globalToolBinder?.activeTool_ || viewer?.toolBinder?.activeTool_) return true;
    } catch (_) { /* fall through to the DOM check */ }
    return !!document.querySelector('.neuroglancer-tool-status');
}

function onKeyDown(e: KeyboardEvent) {
    if (editing.value) return;
    const tag = (document.activeElement as HTMLElement)?.tagName;
    const isTyping = tag === 'INPUT' || tag === 'TEXTAREA' || (document.activeElement as HTMLElement)?.isContentEditable;
    if ((e.code === 'Enter' || e.code === 'Space') && viewerToolActive()) return;

    if (e.code === 'Enter' && !isTyping) {
        e.preventDefault();
        e.stopPropagation();
        if (props.last && !props.step.noConfetti) {
            launchConfetti();
        }
        emit('next');
    }
    if (props.step.spaceAdvances && e.code === 'Space' && !isTyping) {
        e.preventDefault();
        e.stopPropagation();
        emit('next');
    }
}

// A practice step fires this when the learner's edit lands (Amy: "merge
// success!" deserves a celebration).
function onCelebrate() { launchConfetti(); }

// Drag the box out of the way (Amy: the cut box hid the 3D view). The
// handle is the title bar; the offset is added to the computed position and
// resets with the next step, since each step is a fresh component.
const dragOffset = ref({ x: 0, y: 0 });
// Drag from ANYWHERE on the box (Amy 2026-09-28: "click anywhere in the box
// to drag, not just at the top"). Controls keep working: presses on buttons,
// links, fields, videos or anything marked data-no-drag never start a drag,
// and a drag only begins after a 4px move, so a plain click on text or a
// clickable span inside the step's html still acts as a click.
const NO_DRAG = 'button, a, input, textarea, select, option, label, video, [contenteditable], [data-no-drag], .nge-no-drag';
const dragging = ref(false);
let dragStart: { x: number; y: number; ox: number; oy: number } | null = null;
function onDragStart(e: PointerEvent) {
    if (e.button !== 0 || (e.target as HTMLElement).closest(NO_DRAG)) return;
    dragStart = { x: e.clientX, y: e.clientY, ox: dragOffset.value.x, oy: dragOffset.value.y };
    window.addEventListener('pointermove', onDragMove);
    window.addEventListener('pointerup', onDragEnd);
    window.addEventListener('pointercancel', onDragEnd);
}
function onDragMove(e: PointerEvent) {
    if (!dragStart) return;
    const dx = e.clientX - dragStart.x, dy = e.clientY - dragStart.y;
    if (!dragging.value) {
        if (Math.hypot(dx, dy) < 4) return;
        dragging.value = true;
        window.getSelection()?.removeAllRanges();
    }
    e.preventDefault();
    dragOffset.value = { x: dragStart.ox + dx, y: dragStart.oy + dy };
}
function onDragEnd() {
    dragStart = null;
    dragging.value = false;
    window.removeEventListener('pointermove', onDragMove);
    window.removeEventListener('pointerup', onDragEnd);
    window.removeEventListener('pointercancel', onDragEnd);
}
onUnmounted(onDragEnd);

// The first clamp runs before the step's image or video has loaded, so the
// chip then grew past the top of the window ("Merge success!" and "Place the
// points" sat 100px off screen on a 13 inch laptop). Re-clamp on every resize
// of the chip, and when the window itself resizes.
let chipObserver: ResizeObserver | null = null;
let observedChip: Element | null = null;
function observeChip() {
    const el = root.value?.querySelector('.chip:not(.exitConfirm)') ?? null;
    if (el === observedChip) return;
    chipObserver?.disconnect();
    observedChip = el;
    if (el) {
        chipObserver ??= new ResizeObserver(() => { if (!dragging.value) clampChip(); });
        chipObserver.observe(el);
    }
}
// A step whose words follow what is happening (the proofreading test's
// result box: "Scoring your cell", then the score) shows the new words where
// it stands. The box used to keep what it said when it opened, so a score
// that came back still read "one moment" (Ames 2026-10-29: "I submitted but
// no score?").
watch(() => [props.step.title, props.step.text, props.step.html, props.step.nextLabel, props.step.image] as const, async ([title, text, html0, nextLabel, image]) => {
    if (!computedStep.value.title && !computedStep.value.html) return; // not placed yet: updateChipPosition fills it
    let html = html0;
    if (text) html = await marked.parse(text);
    if (html) html = html.replace(/<a\s+(?![^>]*target=)([^>]*href="https?:[^>]*)>/gi, '<a target="_blank" rel="noopener" $1>');
    computedStep.value = { ...computedStep.value, title, html, nextLabel, image };
});
watch(ready, (r) => { if (r) nextTick(observeChip); });
watch(() => computedStep.value, () => nextTick(observeChip));

onMounted(() => {
    window.addEventListener('keydown', onKeyDown, true);
    window.addEventListener('resize', onReclamp);
    document.addEventListener('nge:tutorial-celebrate', onCelebrate);
    document.addEventListener('nge:tutorial-reclamp', onReclamp);
});
onUnmounted(() => {
    chipObserver?.disconnect();
    window.removeEventListener('resize', onReclamp);
    document.removeEventListener('nge:tutorial-celebrate', onCelebrate);
    document.removeEventListener('nge:tutorial-reclamp', onReclamp);
});

onUnmounted(() => {
    window.removeEventListener('keydown', onKeyDown, true);
    // Clean up any injected tutorial highlight styles
    document.getElementById('nge-tutorial-highlight')?.remove();
    if (highlightedEl) {
        highlightedEl.classList.remove('nge-tour-target');
        highlightedEl = null;
    }
});


/** A step can offer text to copy: <button data-nge-copy="75744, 56704, 656">.
 *  Coordinates to paste are handed over this way instead of being typed or
 *  remembered (Ames 2026-10-18: "I didn't copy them!! ... give me coords to
 *  copy, paste"). */
/** A hint "?" shows its text in one pop up, placed on screen above the
 *  mark (or below it near the top), outside the box so the box's edge cannot
 *  cut it off (Ames 2026-10-19: two hints at once, clipped). */
let hintPop: HTMLElement | null = null;
function hideHint() { hintPop?.remove(); hintPop = null; }
function onHtmlOver(e: MouseEvent) {
  const mark = (e.target as HTMLElement | null)?.closest?.('.nge-tut-hint') as HTMLElement | null;
  if (!mark) return;
  hideHint();
  const pop = document.createElement('div');
  pop.className = 'nge-tut-hint-pop';
  pop.textContent = mark.dataset.hint || '';
  document.body.appendChild(pop);
  const r = mark.getBoundingClientRect(), p = pop.getBoundingClientRect();
  const left = Math.max(8, Math.min(window.innerWidth - p.width - 8, r.left + r.width / 2 - p.width / 2));
  const above = r.top - p.height - 8;
  pop.style.left = `${left}px`;
  pop.style.top = `${above >= 8 ? above : r.bottom + 8}px`;
  hintPop = pop;
  mark.addEventListener('mouseleave', hideHint, { once: true });
}
onUnmounted(hideHint);

// Next pressed before the step's action: say so once (Tutorial.vue).
function onNudge() {
  const html = root.value?.querySelector('.chip .html');
  const chip = root.value?.querySelector('.chip');
  if (html && !html.querySelector('.nge-tut-nudge')) {
    const p = document.createElement('p');
    p.className = 'nge-tut-nudge';
    p.textContent = "Give it a try first! Press Next again to skip this one.";
    html.appendChild(p);
  }
  chip?.classList.remove('nge-chip--nudge');
  void (chip as HTMLElement | undefined)?.offsetWidth;
  chip?.classList.add('nge-chip--nudge');
}
document.addEventListener('nge:tutorial-nudge', onNudge);
onUnmounted(() => document.removeEventListener('nge:tutorial-nudge', onNudge));

async function onHtmlClick(e: MouseEvent) {
  const mark = (e.target as HTMLElement | null)?.closest?.('.nge-tut-hint[data-show]') as HTMLElement | null;
  if (mark) {
    for (const el of Array.from(document.querySelectorAll<HTMLElement>(mark.dataset.show || ''))) {
      el.classList.add('nge-tour-target');
      setTimeout(() => el.classList.remove('nge-tour-target'), 5000);
    }
    return;
  }
  const btn = (e.target as HTMLElement | null)?.closest?.('[data-nge-copy]') as HTMLElement | null;
  if (!btn) return;
  e.preventDefault();
  const text = btn.dataset.ngeCopy || '';
  try { await navigator.clipboard.writeText(text); } catch { /* the numbers stay on screen to copy by hand */ }
  const was = btn.textContent;
  btn.textContent = 'Copied ✓';
  btn.classList.add('nge-tut-copy--done');
  setTimeout(() => { btn.textContent = was; btn.classList.remove('nge-tut-copy--done'); }, 1600);
}
</script>

<template>
    <div v-if="ready" ref="root" class="introductionStep" :class="{ hasVideo: computedStep.video !== undefined }">
        <div v-if="computedStep.modal" class="nge-overlay-blocker" @mousedown.stop.prevent></div>
        <!-- Hidden until it has a position: while the step's target was
             still being looked up, an empty box sat in the top left corner
             (Ames, site tour box 4, 2026-10-02). -->
        <div class="ng-extend introductionStepAnchor chipBuildIn"
            :class="[computedStep.cssClass, { 'nge-no-arrow': !!step.highlight && tutorialStore.activeTutorial === 4, 'nge-quick-anim': !computedStep.modal }]"
            :style="{ left: computedStep.left, top: computedStep.top, visibility: computedStep.left ? undefined : 'hidden', transform: dragOffset.x || dragOffset.y ? `translate(${dragOffset.x}px, ${dragOffset.y}px)` : undefined }">
            <div class="arrow"><svg viewBox="0 0 12 14" width="12" height="14" aria-hidden="true"><line class="nge-lead-line" x1="6" y1="5" x2="6" y2="15"/><circle class="nge-lead-dot" cx="6" cy="3" r="2.6"/></svg></div>

            <div v-if="!inExitConfirm" class="chip"
                :class="{ modal: computedStep.modal, noborder: computedStep.noborder, 'nge-chip--dragging': dragging }" :style="chipBounds"
                @pointerdown="onDragStart">
                <span class="corner corner-tl"></span>
                <span class="corner corner-tr"></span>
                <span class="corner corner-bl"></span>
                <span class="corner corner-br"></span>
                <span class="nge-chip-scan" aria-hidden="true"></span>
                <div class="nge-chip-drag" title="Drag anywhere on this box to move it">
                    <span class="nge-chip-grip" aria-hidden="true">⠿</span>
                    <span class="nge-chip-readout"><template v-if="partLabel">{{ partLabel }} · </template>{{ String(stepIndex + 1).padStart(2, '0') }} / {{ String(totalSteps).padStart(2, '0') }}</span>
                </div>
                <button class="exit" @click="inExitConfirm = true">×</button>
                <button v-if="backend.showAdminUi && boxIndex != null" type="button" class="nge-tut-pencil" title="Edit the words of this box (admins)" aria-label="Edit this box" @click="editing = true"><svg viewBox="0 0 16 16" width="13" height="13" aria-hidden="true"><path d="M11.3 2.3a1.4 1.4 0 0 1 2 2L5.6 12 2.5 13.5 4 10.4z" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/></svg></button>
                <div class="title" v-if="shownTitle">
                  <span v-if="computedStep.titleIcon" class="title-icon" v-html="computedStep.titleIcon"></span>
                  <span v-for="(char, i) in [...shownTitle]" :key="i"
                    class="title-letter"
                    :style="{ animationDelay: (computedStep.modal ? i * 0.06 : Math.min(i * 0.015, 0.18)) + 's' }"
                  >{{ char === ' ' ? '\u00A0' : char }}</span>
                </div>
                <video v-if="computedStep.video" width="350" height="242.81" autoplay loop muted playsinline
                    :src="computedStep.video"></video>
                <img class="image" v-if="computedStep.image" :src="computedStep.image">
                <div class="html" v-if="shownHtml" v-html="shownHtml" @click="onHtmlClick" @mouseover="onHtmlOver"></div>
                <div class="nge-chip-foot">
                    <div class="buttonContainer">
                        <button v-if="!computedStep.first" @click="$emit('back')" class="back">back</button>
                        <span class="stepCounter">{{ stepIndex + 1 }}/{{ totalSteps }}</span>
                        <button v-if="computedStep.last" @click="if (!step.noConfetti) launchConfetti(); $emit('next')" class="next" :title="nextTip">{{ computedStep.nextLabel || 'done' }}</button>
                        <button v-else @click="$emit('next')" class="next" :title="nextTip">{{ computedStep.nextLabel || 'next' }}</button>
                    </div>
                    <div class="progressBarContainer nge-chip-rail" :aria-label="`Step ${stepIndex + 1} of ${totalSteps}`">
                        <i v-for="n in totalSteps" :key="n" :class="{ on: n <= stepIndex + 1, now: n === stepIndex + 1 }"></i>
                    </div>
                </div>
            </div>

            <div v-if="inExitConfirm" class="chip exitConfirm" :class="{ modal: computedStep.modal }"
                :style="chipBounds">
                <div class="nge-exit-ask">Exit the tutorial?</div>
                <div class="nge-exit-sub">You can start it again any time: the tutorials live in the <span class="nge-tut-burger" v-html="RESOURCES_MENU_SVG"></span> hamburger menu, top right.</div>
                <div class="buttonContainer">
                    <button @click="inExitConfirm = false" class="nge-hud-btn nge-hud-btn--quiet">Keep going</button>
                    <button @click="$emit('exitIntro')" class="nge-hud-btn">Exit</button>
                </div>
            </div>
        </div>
        <TutorialEditDialog v-if="editing && boxIndex != null" :tutorial="tutorialNum" :box="boxIndex" :step="step" @close="editing = false" />
        <div v-if="computedStep.floatingImage" class="floatingImageRise">
            <img class="floatingImageSway" :src="computedStep.floatingImage">
        </div>
    </div>
</template>

<style scoped>
.nge-chip-drag {
    position: absolute;
    top: 6px;
    left: 14px;
    font-size: 10px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: rgba(126, 202, 255, 0.55);
    cursor: grab;
    user-select: none;
    line-height: 1;
    padding: 4px 6px;
    z-index: 3;
    touch-action: none;
}
.nge-chip-drag:hover { color: #7ecaff; }
/* The whole box is the drag handle; controls inside keep their own cursor. */
.chip { cursor: grab; }
.chip :is(button, a, input, textarea, select, label, video) { cursor: pointer; }
.chip :is(input, textarea) { cursor: text; }
.chip.nge-chip--dragging, .chip.nge-chip--dragging * { cursor: grabbing !important; user-select: none; }

.nge-overlay-blocker {
    z-index: 89;
}

.introductionStepAnchor {
    position: absolute;
    z-index: 90;
    /* filter: drop-shadow(0 8px 4px rgba(0, 0, 0, 0.25)); overwrites backdrop-filter*/
}

/* Beside its target, the box starts just above it and the arrow points at
   it, so a target near the top or bottom of the screen keeps its box next
   to it (Ames 2026-10-18: "next to the element it points to"). The clamp
   then only ever slides the box along its target's edge. */
.introductionStepAnchor.right>* {
    transform: translate(0, -50%);
}
/* The pointer: a leader line, as on an instrument readout, from the box's
   edge to a lit dot on the target's edge (Ames 2026-10-20: the tab was
   "hideous"; earlier ones covered the target or sat off it). Drawn pointing
   up and turned for each side. */
.introductionStepAnchor .arrow {
    position: absolute;
    width: 12px;
    height: 14px;
    z-index: 3;
    border: none;
}
.introductionStepAnchor .arrow svg { display: block; overflow: visible; }
.introductionStepAnchor .arrow .nge-lead-line { stroke: rgba(126, 214, 255, 0.85); stroke-width: 1.5; }
.introductionStepAnchor .arrow .nge-lead-dot {
    fill: #c8f1ff; filter: drop-shadow(0 0 4px rgba(110, 210, 255, 0.95));
    animation: nge-lead-pulse 1.1s ease-in-out 2;
}
@keyframes nge-lead-pulse { 50% { fill: #ffffff; filter: drop-shadow(0 0 9px rgba(110, 210, 255, 1)); } }
@media (prefers-reduced-motion: reduce) { .introductionStepAnchor .arrow .nge-lead-dot { animation: none; } }
/* A box in the middle of the screen points at nothing. */
.introductionStepAnchor.center .arrow { display: none; }
/* Box below its target: the line runs up from the box's top edge. */
.introductionStepAnchor.bottom .arrow { left: -6px; top: 0px; transform: none !important; }
/* Box right of its target: runs left. */
.introductionStepAnchor.right .arrow { left: 1px; top: -7px; transform: rotate(-90deg) !important; }
/* Box left of its target: runs right. */
.introductionStepAnchor.left .arrow { left: -13px; top: -7px; transform: rotate(90deg) !important; }
/* Box above its target: runs down. */
.introductionStepAnchor.top .arrow { left: -6px; top: -14px; transform: rotate(180deg) !important; }

.introductionStepAnchor.right .chip { transform: translate(0, -36px); }

.introductionStepAnchor.left>* {
    transform: translate(-100%, -50%);
}
.introductionStepAnchor.left .chip { transform: translate(0, -36px); }

.introductionStepAnchor.top>*,
.introductionStepAnchor.bottom>* {
    transform: translate(-50%, 0);
}

.introductionStepAnchor.center>* {
    transform: translate(-50%, -50%);
}



.introductionStepAnchor.right .chip {
    left: 14px !important;
}


.introductionStepAnchor.bottom .chip {
    top: 14px !important;
}


.introductionStepAnchor.left .chip {
    right: 14px !important;
    left: auto !important;
}



.chip {
    position: absolute;
    width: auto;
    max-width: min(80vw, calc(100vw - 24px));
    /* Tall steps scroll instead of cropping on short laptop screens, but
       without a visible scrollbar (Amy): the wheel still works. */
    max-height: calc(100vh - 90px);
    overflow-y: auto;
    scrollbar-width: none;
    color: #d0e8ff;
    padding: 30px;
    padding-bottom: 20px;
    border-radius: 8px;
    display: grid;
    justify-items: center;
    font-size: 18px;
    font-weight: 300;
    grid-row-gap: 15px;

    border: 1px solid rgba(160, 200, 240, 0.22);
    /* Higher base opacity so the chip reads cleanly even when it floats
       over a busy panel (segment list, tab bar, etc.). */
    background: linear-gradient(135deg, rgba(8, 12, 26, 0.95), rgba(14, 10, 36, 0.93));
    backdrop-filter: blur(20px) saturate(1.1);
    box-shadow: 0 0 20px rgba(80, 140, 255, 0.1), 0 8px 32px rgba(0, 0, 0, 0.45);
}

/* Microchip-inspired corner traces */
.corner {
    position: absolute;
    pointer-events: none;
    z-index: 2;
}

/* L-shaped corner bracket */
.corner-tl {
    top: -1px;
    left: -1px;
    width: 20px;
    height: 20px;
    border-top: 1.5px solid rgba(160, 200, 240, 0.55);
    border-left: 1.5px solid rgba(160, 200, 240, 0.55);
}

/* Short trace extending from top-left corner */
.corner-tl::before {
    content: '';
    position: absolute;
    top: -1.5px;
    left: 20px;
    width: 8px;
    height: 0;
    border-top: 1.5px solid rgba(160, 200, 240, 0.3);
}

.corner-tl::after {
    content: '';
    position: absolute;
    top: 20px;
    left: -1.5px;
    width: 0;
    height: 8px;
    border-left: 1.5px solid rgba(160, 200, 240, 0.3);
}

.corner-tr {
    top: -1px;
    right: -1px;
    width: 20px;
    height: 20px;
    border-top: 1.5px solid rgba(160, 200, 240, 0.55);
    border-right: 1.5px solid rgba(160, 200, 240, 0.55);
}

.corner-tr::before {
    content: '';
    position: absolute;
    top: -1.5px;
    right: 20px;
    width: 8px;
    height: 0;
    border-top: 1.5px solid rgba(160, 200, 240, 0.3);
}

.corner-bl {
    bottom: -1px;
    left: -1px;
    width: 20px;
    height: 20px;
    border-bottom: 1.5px solid rgba(160, 200, 240, 0.55);
    border-left: 1.5px solid rgba(160, 200, 240, 0.55);
}

.corner-bl::before {
    content: '';
    position: absolute;
    bottom: -1.5px;
    left: 20px;
    width: 8px;
    height: 0;
    border-bottom: 1.5px solid rgba(160, 200, 240, 0.3);
}

.corner-br {
    bottom: -1px;
    right: -1px;
    width: 20px;
    height: 20px;
    border-bottom: 1.5px solid rgba(160, 200, 240, 0.55);
    border-right: 1.5px solid rgba(160, 200, 240, 0.55);
}

.corner-br::before {
    content: '';
    position: absolute;
    bottom: -1.5px;
    right: 20px;
    width: 8px;
    height: 0;
    border-bottom: 1.5px solid rgba(160, 200, 240, 0.3);
}

@keyframes chipBuildIn {
    0% {
        opacity: 0;
        transform: translate(var(--tx, -50%), var(--ty, -50%)) scale(0.92);
    }
    100% {
        opacity: 1;
        transform: translate(var(--tx, -50%), var(--ty, -50%)) scale(1);
    }
}

.chipBuildIn {
    animation: chipFadeIn 0.3s ease-out both;
}

@keyframes chipFadeIn {
    0% {
        opacity: 0;
        filter: blur(4px);
    }
    100% {
        opacity: 1;
        filter: blur(0px);
    }
}

.hasVideo .chip:not(.exitConfirm) {
    padding: 8px 8px 20px 8px;
}

/* Modal steps (the site tour's welcome and finale) used to drop the box's
   padding for a full-bleed video that is long gone. Without it the title
   sat on the drag handle and Back, the counter and the progress bar ran to
   the edges (Ames, 2026-10-05). They keep the normal padding now, and
   scroll like any other box on a short screen. */
.chip.modal {
    padding-top: 34px;
}

.chip .title {
    font-size: 22px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-wrap: wrap;
    gap: 2px;
}

@keyframes letterReveal {
    0% { opacity: 0; transform: translateY(8px) scale(0.8); }
    60% { opacity: 1; transform: translateY(-2px) scale(1.05); }
    100% { opacity: 1; transform: translateY(0) scale(1); }
}

.title-letter {
    display: inline-block;
    opacity: 0;
    animation: letterReveal 0.4s ease-out forwards;
}

/* Toolbar icon shown alongside the title (e.g. the same Find Path icon
   that lives in the top toolbar). Sized to match the title font and
   pulled into rhythm with the letter-by-letter reveal. */
.title-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin-right: 8px;
    opacity: 0;
    animation: letterReveal 0.4s ease-out 0s forwards;
}
.title-icon :deep(svg) {
    width: 26px;
    height: 26px;
    vertical-align: middle;
}

.chip video,
.chip .image {
    width: 100%;
    /* On a 13 inch laptop the 480px step pictures pushed the text and the
       Next button below the fold of the (scrollbar-less) chip. The picture
       gives up height first so the words and controls always fit. */
    max-height: max(120px, calc(100vh - 450px));
    object-fit: contain;
}
/* Pictures written into a step's html (the before/after pair) shrink too. */
.chip .html img {
    max-height: max(100px, calc(100vh - 500px));
    object-fit: contain;
}
/* Back / Next always stay in view. A step with a long list can still be
   taller than a laptop window, and the chip scrolls without a scrollbar, so
   the controls ride on the bottom edge and the words scroll up under them. */
.ng-extend .chip .nge-chip-foot {
    position: sticky;
    bottom: -20px;
    z-index: 2;
    width: 100%;
    margin: 0 0 -20px;
    padding: 6px 0 20px;
    background: rgb(7, 11, 23);
}
/* A short fade above the bar so words scrolling under it dissolve. */
.ng-extend .chip .nge-chip-foot::before {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: 100%;
    height: 22px;
    background: linear-gradient(to bottom, rgba(7, 11, 23, 0), rgb(7, 11, 23));
    pointer-events: none;
}

.ng-extend .chip .buttonContainer {
    display: grid;
    position: relative;
    width: 100%;
    align-items: center;
}

.ng-extend .chip.exitConfirm .buttonContainer {
    width: auto;
    grid-auto-flow: column;
    grid-column-gap: 10px;
}

.ng-extend .chip .buttonContainer button {
    text-transform: uppercase;
}

.ng-extend .chip button.next {
    justify-self: center;
    border-radius: 999px;
    border: 1px solid var(--color-small-text);
    padding: 2px 16px;
}

.ng-extend .chip button.back {
    position: absolute;
    text-transform: uppercase;
    font-style: italic;
    font-size: 14px;
    opacity: 0.5;
}

.ng-extend .chip button.back:hover {
    background: none;
    opacity: 1;
}

.ng-extend .chip .progressBarContainer {
    width: 100%;
    height: 3px;
    background: rgba(255, 255, 255, 0.08);
    border-radius: 2px;
    /* Room between the buttons and the bar (Ames: "a tad higher"). */
    margin-top: 16px;
    overflow: hidden;
}

.ng-extend .chip .progressBar {
    height: 100%;
    background: linear-gradient(90deg, #00c8ff, #80ffcc);
    border-radius: 2px;
    transition: width 0.5s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.ng-extend .chip .stepCounter {
    position: absolute;
    right: 0;
    bottom: 2px;
    font-size: 12px;
    opacity: 0.4;
    font-family: 'Inter', 'Roboto', monospace;
    letter-spacing: 0.5px;
}

.ng-extend .chip button.exit {
    position: absolute;
    right: 0;
    border: none;
    padding: 0;
    opacity: 0.75;
    transition: opacity 0.2s;
    font-size: 22px;
    line-height: 22px;
    font-weight: 300;
    width: 22px;
    margin: 6px 6px 0 0;
    z-index: 1;
}

.ng-extend .chip button.exit:hover {
    background-color: initial;
    opacity: 1;
}

.ng-extend .chip button:hover {
    background-color: rgba(255, 255, 255, 0.25);
}

.chip :deep(a) {
    color: #7ecaff;
}

.chip :deep(a:hover) {
    color: #b0dfff;
}

/* Balloon: outer div rises smoothly, inner img sways side-to-side */
@keyframes floatUp {
    0% {
        transform: translateY(0);
        opacity: 1;
    }
    85% {
        opacity: 1;
    }
    100% {
        transform: translateY(-120vh);
        opacity: 0;
    }
}

@keyframes floatSway {
    0%   { transform: translateX(0)    rotate(0deg); }
    25%  { transform: translateX(18px) rotate(1.5deg); }
    50%  { transform: translateX(0)    rotate(0deg); }
    75%  { transform: translateX(-18px) rotate(-1.5deg); }
    100% { transform: translateX(0)    rotate(0deg); }
}

.floatingImageRise {
    position: fixed;
    bottom: -350px;
    left: 50%;
    margin-left: -150px;
    width: 300px;
    z-index: 91;
    pointer-events: none;
    animation: floatUp 8s linear forwards;
}

.floatingImageSway {
    width: 100%;
    animation: floatSway 3s ease-in-out infinite;
}

/* ── Site Tour: highlight ring + no-arrow + quick build-in ─────────── */

/* Hide the chip's pointer arrow when the step uses an element highlight */
.introductionStepAnchor.nge-no-arrow .arrow {
    display: none;
}

/* Faster entrance for non-modal element-pointer cards: skip the
   long letter-by-letter stagger and just fade in. */
.introductionStepAnchor.nge-quick-anim {
    animation: chipFadeIn 0.18s ease-out both;
}

/* ══ Instrument restyle (Ames 2026-10-05: "sleek, like something made 100
   years in the future") ══════════════════════════════════════════════════
   Built from the scifi-ui set rather than invented: the holopanel surface
   (dark gradient, lit top hairline, inset rim, materialise entrance), one
   scan pass on arrival that never loops, a mono step readout, and a
   segmented rail that reports the real step. These rules sit last so they
   win over the older ones above at equal weight. */
.chip {
    color: rgba(226, 238, 251, 0.88);
    padding: 46px 28px 20px;
    border-radius: 12px;
    justify-items: stretch;
    /* Grows with the window: 15px read as tiny on a large monitor (Ames and
       Nik, 2026-10-08, "waaayyy too small"). 15px up to about 1750px wide,
       then in step with the width, to 24px at most. */
    font-size: clamp(15px, 0.86vw, 24px);
    line-height: 1.55;
    font-weight: 400;
    grid-row-gap: 14px;
    border: 1px solid rgba(74, 150, 224, 0.36);
    /* Flat from 70% down, so the sticky footer (same colour) has no seam. */
    background: linear-gradient(180deg, rgba(14, 21, 36, 0.97) 0%, rgba(6, 10, 19, 0.98) 70%);
    backdrop-filter: blur(14px) saturate(1.2);
    box-shadow:
        0 18px 50px rgba(0, 0, 0, 0.55),
        0 0 60px rgba(74, 150, 224, 0.10),
        inset 0 1px 0 rgba(196, 228, 255, 0.10);
}
/* The lit hairline along the top edge. */
.chip::before {
    content: '';
    position: absolute;
    left: 8%;
    right: 8%;
    top: 0;
    height: 1px;
    pointer-events: none;
    background: linear-gradient(90deg, transparent, rgba(196, 228, 255, 0.95) 50%, transparent);
    box-shadow: 0 0 12px rgba(178, 216, 248, 0.6);
    z-index: 3;
}
.corner { border-color: rgba(126, 224, 255, 0.7) !important; }
.corner::before, .corner::after { border-color: rgba(126, 224, 255, 0.28) !important; }

/* Entrance: the panel materialises out of an overbright blur. Opacity and
   filter only, because the anchor's transform belongs to dragging. */
.chipBuildIn,
.introductionStepAnchor.nge-quick-anim {
    animation: nge-chip-in 520ms cubic-bezier(0.16, 1, 0.3, 1) both;
}
@keyframes nge-chip-in {
    0%   { opacity: 0; filter: blur(10px) brightness(2.4); }
    60%  { opacity: 1; filter: blur(0) brightness(1.15); }
    100% { opacity: 1; filter: none; }
}
/* One scan line on arrival. It passes once: a band that loops is a status
   light. Moves by `top`, not transform, so it adds no scroll height. */
.nge-chip-scan {
    position: absolute;
    left: 0;
    right: 0;
    top: -9%;
    height: 9%;
    z-index: 2;
    pointer-events: none;
    opacity: 0;
    background: linear-gradient(180deg, transparent, rgba(126, 224, 255, 0.14), transparent);
    animation: nge-chip-scan 1500ms cubic-bezier(0.22, 0.9, 0.28, 1) 260ms 1 both;
}
@keyframes nge-chip-scan {
    0%   { top: -9%; opacity: 0; }
    12%  { opacity: 1; }
    88%  { opacity: 1; }
    100% { top: 91%; opacity: 0; }
}

/* Header strip: grip and a mono step readout. */
.nge-chip-drag {
    top: 15px;
    left: 28px;
    /* clear of the close button, whatever the font (a player's Firefox ran
       the step count under the x, 2026-10-20) */
    right: 44px;
    overflow: hidden;
    white-space: nowrap;
    padding: 0;
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 10px;
    letter-spacing: 0.2em;
    color: rgba(126, 224, 255, 0.62);
}
.nge-chip-grip { font-size: 11px; letter-spacing: 0; opacity: 0.7; }
.nge-chip-readout {
    font-family: ui-monospace, 'SFMono-Regular', Menlo, Consolas, monospace;
    font-variant-numeric: tabular-nums;
}
.ng-extend .chip .stepCounter { display: none; }
.ng-extend .chip button.exit {
    top: 8px;
    right: 8px;
    margin: 0;
    width: 26px;
    height: 26px;
    line-height: 24px;
    font-size: 20px;
    border-radius: 4px;
    color: rgba(226, 238, 251, 0.7);
    opacity: 1;
    transition: background 0.2s, color 0.2s;
}
.ng-extend .chip button.exit:hover { background: rgba(196, 228, 255, 0.14); color: #fff; }
/* The admins' pencil, left of the close button. */
.ng-extend .chip button.nge-tut-pencil {
    position: absolute;
    top: 9px;
    right: 38px;
    z-index: 4;
    width: 24px;
    height: 24px;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    border: 0;
    border-radius: 4px;
    color: rgba(226, 238, 251, 0.45);
    cursor: pointer;
}
.ng-extend .chip button.nge-tut-pencil:hover { background: rgba(196, 228, 255, 0.14); color: #fff; }

/* Title: a HUD label with a diamond marker and a hairline that fades out. */
.chip .title {
    justify-content: flex-start;
    gap: 0;
    font-size: 15px;
    font-weight: 600;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    line-height: 1.3;
    color: #eef8ff;
    text-shadow: 0 0 14px rgba(126, 224, 255, 0.35);
    padding-bottom: 12px;
    border-bottom: 1px solid transparent;
    border-image: linear-gradient(90deg, rgba(126, 224, 255, 0.7), rgba(126, 224, 255, 0.10) 62%, transparent) 1;
}
.chip .title::before {
    content: '';
    flex: none;
    width: 6px;
    height: 6px;
    margin-right: 12px;
    transform: rotate(45deg);
    background: #7ee0ff;
    box-shadow: 0 0 8px rgba(126, 224, 255, 0.9);
}
.title-icon { margin-right: 10px; }
/* Wide hero boxes and video boxes keep the title clear of the header strip. */
.chip.modal { padding-top: 46px; }
.hasVideo .chip:not(.exitConfirm) .title { margin: 38px 20px 0; }

.chip .html :deep(p) { margin: 0 0 0.7em; }
.chip .html :deep(p:last-child) { margin-bottom: 0; }
.chip .html :deep(strong) { color: #ffffff; font-weight: 600; }

/* Footer: Back on the left, the action on the right, segmented rail below. */
.ng-extend .chip .nge-chip-foot { background: rgb(6, 10, 19); padding-top: 8px; }
.ng-extend .chip .nge-chip-foot::before { background: linear-gradient(to bottom, rgba(6, 10, 19, 0), rgb(6, 10, 19)); }
.ng-extend .chip .buttonContainer {
    display: flex;
    align-items: center;
    min-height: 32px;
}
.ng-extend .chip.exitConfirm .buttonContainer { gap: 10px; justify-content: center; }
.ng-extend .chip button.next {
    margin-left: auto;
    border-radius: 3px;
    border: 1px solid rgba(126, 224, 255, 0.55);
    padding: 7px 16px 7px 18px;
    font-size: 11.5px;
    font-weight: 600;
    letter-spacing: 0.2em;
    line-height: 1.2;
    color: #eaf9ff;
    background: linear-gradient(180deg, rgba(126, 224, 255, 0.17), rgba(126, 224, 255, 0.05));
    box-shadow: inset 0 0 12px rgba(126, 224, 255, 0.10);
    transition: background 0.2s, border-color 0.2s, box-shadow 0.2s;
}
.ng-extend .chip button.next::after {
    content: '\203A';
    display: inline-block;
    margin-left: 10px;
    font-size: 15px;
    line-height: 0.8;
    letter-spacing: 0;
    transition: transform 0.2s ease-out;
}
.ng-extend .chip button.next:hover {
    background: linear-gradient(180deg, rgba(126, 224, 255, 0.30), rgba(126, 224, 255, 0.12));
    border-color: rgba(190, 240, 255, 0.95);
    box-shadow: inset 0 0 12px rgba(126, 224, 255, 0.18), 0 0 18px rgba(126, 224, 255, 0.35);
}
.ng-extend .chip button.next:hover::after { transform: translateX(3px); }
.chip.exitConfirm { padding: 26px 28px 22px; grid-row-gap: 6px; min-width: 280px; }
.nge-exit-ask {
    font-size: 15px;
    font-weight: 600;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    color: #eef8ff;
    text-shadow: 0 0 14px rgba(126, 224, 255, 0.35);
}
.nge-exit-sub { font-size: 13px; color: rgba(196, 228, 255, 0.75); }
.ng-extend .chip.exitConfirm .buttonContainer { margin-top: 12px; justify-content: flex-end; }
.ng-extend .chip button.back {
    position: static;
    font-style: normal;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.2em;
    padding: 7px 0;
    color: rgba(196, 228, 255, 0.85);
    opacity: 0.6;
    transition: opacity 0.2s;
}
.ng-extend .chip button.back::before { content: '\2039'; margin-right: 8px; font-size: 15px; line-height: 0.8; letter-spacing: 0; }
.ng-extend .chip button.back:hover { background: none; opacity: 1; }
.ng-extend .chip .nge-chip-rail {
    display: flex;
    gap: 3px;
    height: 3px;
    margin-top: 14px;
    background: none;
    border-radius: 0;
    overflow: visible;
}
.nge-chip-rail i {
    flex: 1 1 0;
    min-width: 0;
    border-radius: 1px;
    background: rgba(196, 228, 255, 0.12);
    transition: background 0.4s, box-shadow 0.4s;
}
.nge-chip-rail i.on { background: rgba(126, 224, 255, 0.7); }
.nge-chip-rail i.now { background: #c4f3ff; box-shadow: 0 0 8px rgba(126, 224, 255, 0.95); }

@media (prefers-reduced-motion: reduce) {
    .chipBuildIn, .introductionStepAnchor.nge-quick-anim { animation: none; }
    .nge-chip-scan { animation: none; }
}
</style>

<style>
/* UNSCOPED: target elements outside this component (toolbar buttons,
   side panels, etc.) get a pulsing cyan ring while they're the focus
   of the active tour step. */
.nge-tour-target {
    position: relative;
    z-index: 88;
    outline: 2px solid rgba(0, 200, 255, 0.85) !important;
    outline-offset: 3px;
    border-radius: 6px;
    animation: nge-tour-pulse 1.6s ease-in-out infinite !important;
    transition: outline-color 0.2s;
}
/* A ring of light that breathes around the target, drawn on its own layer
   so a button's own shadow or animation cannot hide it (Ames 2026-10-19:
   "the top bar icon is not glowing"). Inputs have no ::after and keep the
   outline above. */
.nge-tour-target::after {
    content: '';
    position: absolute;
    inset: -6px;
    border-radius: 9px;
    border: 2px solid rgba(110, 225, 255, 0.95);
    pointer-events: none;
    animation: nge-tour-ring 1.4s ease-in-out infinite;
}
@keyframes nge-tour-ring {
    0%, 100% { opacity: 0.55; box-shadow: 0 0 8px 1px rgba(0, 200, 255, 0.55), inset 0 0 6px rgba(0, 200, 255, 0.35); transform: scale(1); }
    50%      { opacity: 1;    box-shadow: 0 0 22px 6px rgba(0, 220, 255, 0.9), inset 0 0 10px rgba(0, 220, 255, 0.55); transform: scale(1.08); }
}
@media (prefers-reduced-motion: reduce) { .nge-tour-target::after { animation: none; opacity: 1; } }
@keyframes nge-tour-pulse {
    0%, 100% {
        box-shadow:
            0 0 0 0 rgba(0, 200, 255, 0.45),
            0 0 18px 2px rgba(0, 180, 255, 0.55);
    }
    50% {
        box-shadow:
            0 0 0 6px rgba(0, 200, 255, 0),
            0 0 26px 4px rgba(0, 220, 255, 0.75);
    }
}

/* Welcome step: hero render on top, copy below. The .chip's grid-row-gap
   keeps the spacing between the hero and the buttons consistent with
   every other step. */
.nge-tour-welcome {
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 4px 6px 0;
}
.nge-tour-welcome-hero {
    width: 100%;
    border-radius: 8px;
    overflow: hidden;
    border: 1px solid rgba(120, 180, 240, 0.18);
    background: rgba(0, 0, 0, 0.35);
    line-height: 0;
}
.chip::-webkit-scrollbar {
    display: none;
}
.nge-tour-welcome-hero img {
    display: block;
    width: 100%;
    height: auto;
    max-height: 280px;
    object-fit: cover;
    object-position: center;
}
/* A square card shown whole (the Retina tour's welcome): it shrinks on a
   short window instead of being cropped to a band. */
.chip .html .nge-tour-welcome-hero--whole img {
    object-fit: contain;
    max-height: max(180px, min(436px, calc(100vh - 430px)));
    background: #03040a;
}
/* Dataset tour cell cards: a picture on top, "show all" buttons below. */
/* A live page in a tour card (CA3's interactive action potential). */
.chip .html .nge-tour-cell-embed { margin: 0 0 10px; line-height: 0; border: 1px solid rgba(112, 190, 255, 0.2); border-radius: 6px; overflow: hidden; background: #06090f; }
.chip .html .nge-tour-cell-embed iframe { display: block; width: 100%; height: 300px; border: 0; }
.chip .html .nge-tour-cell-img { margin: 0 0 10px; line-height: 0; text-align: center; }
.chip .html .nge-tour-cell-img img { max-width: 100%; max-height: 190px; width: auto; height: auto; }
.chip .html .nge-tour-cell-text p { margin: 0 0 10px; }
.chip .html .nge-tour-more { margin-top: 8px; }
.chip .html .nge-tour-more-btn {
    width: 100%;
    background: rgba(74, 158, 255, 0.1);
    border: 1px solid rgba(74, 158, 255, 0.4);
    border-radius: 6px;
    color: #d6e8ff;
    font: inherit;
    font-size: 0.9em;
    padding: 7px 10px;
    cursor: pointer;
    text-align: left;
}
.chip .html .nge-tour-more-btn:hover { background: rgba(74, 158, 255, 0.2); }
.chip .html .nge-tour-more-btn[data-shown="1"] { background: rgba(74, 158, 255, 0.28); }
.chip .html .nge-tour-more-note { font-size: 0.8em; color: rgba(190, 205, 225, 0.65); margin-top: 3px; }
.nge-tour-welcome-body {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 0 4px;
    line-height: 1.5;
    font-size: 15px;
    color: #dfeeff;
}
/* Weighted to beat the box's own paragraph spacing: the gap above does it. */
.chip .html .nge-tour-welcome-body p { margin: 0; }

/* Final step (Option B): hero render + gratitude. The boxes-of-CTAs
   layout was busy and the toolbar already covers Cell Library / ⌘K /
   Leaderboard one click away — let the gratitude be the payoff. */
.nge-tour-finale {
    display: flex;
    flex-direction: column;
    gap: 14px;
    padding: 4px 6px 0;
    text-align: center;
}
.nge-tour-finale-hero {
    width: 100%;
    border-radius: 8px;
    overflow: hidden;
    border: 1px solid rgba(120, 180, 240, 0.18);
    background: rgba(0, 0, 0, 0.4);
    line-height: 0;
}
.nge-tour-finale-hero img {
    display: block;
    width: 100%;
    height: auto;
    max-height: 260px;
    object-fit: cover;
    object-position: center;
}
.nge-tour-finale-lead {
    margin: 8px 12px 0;
    font-size: 16px;
    line-height: 1.45;
    color: #e6f0ff;
    font-weight: 500;
}
.nge-tour-finale-sub {
    margin: 0 12px;
    font-size: 14px;
    line-height: 1.5;
    color: rgba(220, 232, 250, 0.85);
}
.nge-tour-finale-contact {
    margin: 6px 12px 0;
    font-size: 13px;
    color: rgba(190, 215, 240, 0.75);
}
.nge-tour-finale-contact a {
    color: #9fdcff;
    text-decoration: none;
    border-bottom: 1px solid rgba(159, 220, 255, 0.4);
}
.nge-tour-finale-tag {
    margin: 12px 12px 4px;
    font-size: 18px;
    font-weight: 600;
    letter-spacing: 1.5px;
    text-transform: uppercase;
    color: #9fdcff;
    text-shadow: 0 0 16px rgba(120, 200, 255, 0.5);
}

/* Final-step "ready to explore" grid of next-step cards */
.nge-tour-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;
    max-width: 460px;
    margin: 0 auto;
}
.nge-tour-card {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 14px 12px;
    border: 1px solid rgba(120, 180, 240, 0.22);
    border-radius: 6px;
    background: linear-gradient(135deg, rgba(20, 35, 60, 0.55), rgba(30, 20, 60, 0.45));
    text-align: center;
    transition: border-color 0.2s, transform 0.2s, box-shadow 0.2s;
}
.nge-tour-card:hover {
    border-color: rgba(120, 200, 255, 0.55);
    transform: translateY(-1px);
    box-shadow: 0 4px 18px rgba(80, 160, 255, 0.18);
}
.nge-tour-card-icon {
    font-size: 22px;
    margin-bottom: 4px;
    line-height: 1;
}
.nge-tour-card-title {
    font-size: 13.5px;
    font-weight: 600;
    color: #d6ebff;
    letter-spacing: 0.3px;
}
.nge-tour-card-sub {
    font-size: 11.5px;
    color: rgba(190, 215, 240, 0.65);
    margin-top: 2px;
}
.nge-tut-copy {
  display: inline-flex; align-items: center; gap: 6px; margin: 2px 0; padding: 4px 10px; border-radius: 6px; cursor: pointer;
  font: 600 13px 'JetBrains Mono', ui-monospace, monospace; color: #cfe9ff;
  background: rgba(74, 158, 255, 0.14); border: 1px solid rgba(126, 202, 255, 0.5);
  /* A long set of IDs wraps inside the box instead of running out of it. */
  max-width: 100%; box-sizing: border-box; white-space: normal; overflow-wrap: anywhere; text-align: left;
}
.nge-tut-copy:hover { background: rgba(74, 158, 255, 0.26); }
.nge-tut-copy--done { color: #b9f5c6; border-color: rgba(142, 232, 160, 0.7); }
.nge-tut-checklist li { transition: color 0.3s; }
.nge-tut-checklist li.nge-tut-done { color: #9ff0b8; }
.nge-tut-checklist li.nge-tut-done::marker { content: '✓  '; color: #3dff9a; font-weight: 700; }
/* A pretend Segments row, drawn like the panel's (tutorial-2.ts FAKE_ROW). */
.nge-tut-row {
  display: flex; align-items: center; gap: 10px; margin: 6px 0 2px; padding: 6px 10px;
  background: rgba(10, 16, 30, 0.9); border: 1px solid rgba(126, 202, 255, 0.25); border-radius: 6px;
}
.nge-tut-row-eye { color: #8fb8e8; display: inline-flex; }
.nge-tut-row-id {
  font: 600 11px 'JetBrains Mono', ui-monospace, monospace; color: #04121f;
  background: #35b9ff; border-radius: 3px; padding: 1px 5px;
}
.nge-tut-row-gap { flex: 1; }
.nge-tut-row-fly { color: #9db4d6; }
.nge-tut-row-tri { color: #e8edf5; font-size: 12px; }
.nge-tut-legend { display: grid; gap: 4px; margin: 8px 0 12px; }
.nge-tut-legend > div { display: flex; align-items: center; gap: 10px; }
.nge-tut-key {
  flex: none; display: inline-flex; align-items: center; justify-content: center;
  min-width: 26px; height: 22px; padding: 0 4px; border-radius: 4px;
  background: rgba(74, 158, 255, 0.12); border: 1px solid rgba(126, 202, 255, 0.35); color: #cfe9ff; font-size: 12px;
}
.nge-tut-key--id { background: #35b9ff; color: #04121f; font: 700 10px 'JetBrains Mono', ui-monospace, monospace; border: none; }
/* "Path found!" across the middle of the screen. */
.nge-tut-banner {
  position: fixed; left: 50%; top: 42%; transform: translate(-50%, -50%); z-index: 100002; pointer-events: none;
  font: 800 clamp(34px, 4vw, 64px) 'Orbitron', 'Inter', sans-serif; letter-spacing: 0.06em; color: #b8ffd6;
  text-shadow: 0 0 18px rgba(80, 255, 160, 0.75), 0 0 42px rgba(80, 255, 160, 0.45);
  animation: nge-tut-banner-in 2.6s ease-out both;
}
@keyframes nge-tut-banner-in {
  0% { opacity: 0; transform: translate(-50%, -50%) scale(0.7); }
  15% { opacity: 1; transform: translate(-50%, -50%) scale(1.06); }
  25% { transform: translate(-50%, -50%) scale(1); }
  80% { opacity: 1; }
  100% { opacity: 0; }
}
/* A hint mark on a tutorial line (tutorial-2.ts hint). */
.nge-tut-hint {
  display: inline-flex; align-items: center; justify-content: center; width: 17px; height: 17px; margin-left: 4px;
  border-radius: 50%; border: 1px solid rgba(126, 202, 255, 0.55); color: #9fd4ff; font: 700 11px Inter, sans-serif;
  cursor: help; vertical-align: 2px;
}
.nge-tut-hint { position: relative; }
.nge-tut-hint[data-show] { cursor: pointer; }
.nge-tut-hint:hover { background: rgba(74, 158, 255, 0.25); color: #fff; outline: none; }
.nge-tut-hint-pop {
  position: fixed; z-index: 100003; max-width: 260px; padding: 7px 10px; border-radius: 6px; pointer-events: none;
  background: #0b1424; border: 1px solid rgba(126, 202, 255, 0.5); color: #e6f2ff; box-shadow: 0 8px 24px rgba(0, 0, 0, 0.5);
  font: 500 13px/1.4 Inter, sans-serif;
}
.nge-tut-burger svg { width: 1.15em !important; height: 1.15em !important; vertical-align: -0.2em; }
/* The box's own button rules kept a long copy button on one line, running
   out of the box (Ames 2026-10-19). */
.chip .nge-tut-copy {
  display: inline-flex !important; max-width: 100% !important; white-space: normal !important;
  overflow-wrap: anywhere !important; word-break: break-word; text-align: left !important; height: auto !important;
  line-height: 1.4 !important;
}
/* The pretend row in its hover state, matching the Segments panel. */
.nge-tut-row--hover { background: rgba(30, 48, 80, 0.75); border-color: rgba(126, 202, 255, 0.4); }
.nge-tut-row-id { background: #f5d142; }
.nge-tut-row-trash { color: #ff7a7a; display: inline-flex; }
.nge-tut-row-copy { color: #9db4d6; display: inline-flex; }
.nge-tut-row-tri { position: relative; }
.nge-tut-row-tri i { position: absolute; right: -6px; top: -5px; width: 7px; height: 7px; border-radius: 50%; background: #f5a623; }
.nge-tut-key--trash { color: #ff7a7a; }
/* A layer tab named in the text, drawn like the side panel's tabs. */
.nge-tut-tab {
  display: inline-block; padding: 1px 9px; border-radius: 5px 5px 0 0; font-weight: 600; font-size: 0.92em;
  color: #e8f3ff; background: rgba(40, 70, 110, 0.6); border: 1px solid rgba(126, 202, 255, 0.45); border-bottom-color: transparent;
}
.nge-tut-found { color: #9ff0b8; font-weight: 600; margin-top: 8px; }
/* Pressed Next before doing the step (tutorial-2.ts pending). */
.nge-tut-nudge { color: #ffd27a; font-weight: 600; margin-top: 6px; }
.chip.nge-chip--nudge { animation: nge-chip-nudge 0.45s ease-in-out; }
@keyframes nge-chip-nudge { 0%, 100% { transform: translateX(0); } 25% { transform: translateX(-6px); } 75% { transform: translateX(6px); } }
/* A key to press, written in the text. */
.nge-tut-key-big {
  display: inline-block; padding: 1px 8px; margin: 0 2px; border-radius: 5px; font: 700 0.95em 'JetBrains Mono', ui-monospace, monospace;
  color: #fff; background: rgba(74, 158, 255, 0.25); border: 1px solid rgba(126, 214, 255, 0.8); box-shadow: 0 2px 0 rgba(126, 214, 255, 0.5);
}
/* Sparkles on the eye a step asks for (tutorial-2.ts sparkleEye). */
.nge-tut-sparkle { position: relative; overflow: visible !important; filter: drop-shadow(0 0 6px #fff6a8) drop-shadow(0 0 10px #ffd84d); animation: nge-spark-glow 1s ease-in-out infinite; }
.nge-tut-sparkle::before, .nge-tut-sparkle::after {
  content: '✦'; position: absolute; color: #fff6a8; pointer-events: none; font-size: 10px; line-height: 1;
  animation: nge-spark-twinkle 1.2s ease-in-out infinite;
}
.nge-tut-sparkle::before { left: -9px; top: -8px; }
.nge-tut-sparkle::after { right: -9px; bottom: -7px; animation-delay: 0.6s; font-size: 8px; }
@keyframes nge-spark-glow { 50% { filter: drop-shadow(0 0 10px #fff6a8) drop-shadow(0 0 16px #ffd84d); } }
@keyframes nge-spark-twinkle { 0%, 100% { opacity: 0; transform: scale(0.4) rotate(0deg); } 50% { opacity: 1; transform: scale(1.2) rotate(90deg); } }
@media (prefers-reduced-motion: reduce) { .nge-tut-sparkle, .nge-tut-sparkle::before, .nge-tut-sparkle::after { animation: none; } }
</style>
